import React, { useState, useEffect } from 'react';
import { isPWA, isMobile, getPlatform, trackPWAInstall, getInstallStatus } from '../utils/pwa';

const PWAInstallBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [platform, setPlatform] = useState('');

  useEffect(() => {
    // Check if already installed or dismissed
    const status = getInstallStatus();
    if (status.isInstalled) {
      setIsInstalled(true);
      return;
    }

    // Only show on mobile
    if (!isMobile()) return;

    // Check if running as PWA
    if (isPWA()) {
      setIsInstalled(true);
      localStorage.setItem('pwa_installed', 'true');
      return;
    }

    setPlatform(getPlatform());

    // Listen for install prompt
    const handleInstallReady = (e) => {
      setDeferredPrompt(e.detail.prompt);
      // Only show if not dismissed before
      if (!localStorage.getItem('pwa_dismissed')) {
        setShowBanner(true);
      }
    };

    window.addEventListener('pwa-install-ready', handleInstallReady);

    // Listen for successful install
    const handleInstalled = () => {
      setIsInstalled(true);
      setShowBanner(false);
      localStorage.setItem('pwa_installed', 'true');
      trackPWAInstall();
    };

    window.addEventListener('pwa-installed', handleInstalled);

    // Check if already installed via display-mode
    if (isPWA()) {
      setIsInstalled(true);
      localStorage.setItem('pwa_installed', 'true');
    }

    return () => {
      window.removeEventListener('pwa-install-ready', handleInstallReady);
      window.removeEventListener('pwa-installed', handleInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === 'accepted') {
        console.log('✅ User accepted the install prompt');
        localStorage.setItem('pwa_installed', 'true');
        setShowBanner(false);
        trackPWAInstall();
      } else {
        console.log('❌ User declined the install prompt');
        localStorage.setItem('pwa_dismissed', 'true');
        setShowBanner(false);
      }
      setDeferredPrompt(null);
    } else {
      // Fallback: redirect to install instructions
      window.location.href = '/install';
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa_dismissed', 'true');
    setShowBanner(false);
  };

  if (!showBanner || isInstalled) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-green-600 text-white shadow-lg animate-slide-up">
      <div className="max-w-4xl mx-auto p-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="text-3xl">🌱</div>
          <div>
            <h4 className="font-semibold text-sm">Get the Our Pickup App!</h4>
            <p className="text-xs text-green-100">
              {platform === 'ios' ? 'Add to your home screen for faster access' :
               platform === 'android' ? 'Install the app for a better experience' :
               'Install our app for offline access'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleInstall}
            className="bg-white text-green-700 px-4 py-2 rounded-lg font-medium text-sm hover:bg-green-50 transition"
          >
            {platform === 'ios' ? '📲 Add' : '📲 Install'}
          </button>
          <button
            onClick={handleDismiss}
            className="text-green-200 hover:text-white text-sm px-2"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallBanner;
